import { Header } from "../../components/list/header"
import { Pager } from "../../components/list/pager"
import { ITEMS_PER_PAGE, DEFAULT_IMAGE, getBackendApiUrl, getDWSUrl, getLanguage } from "../../config"
import { convertDesignDocToImageObject, generateImageSrcFromArtwork } from "../../utils/general"
import SkeletonLoader from "../../components/list/skeletonAnimation"
import { getColor } from "../../utils/general"
import { getUrlParams } from "../../utils/general"
import { useSnapshot } from "valtio"
import appModel from "../../models/app-model"
import { addToCart } from "../../utils/cart"
import { DesignDoc } from "../../types/api-types"
import { useEffect, useState } from "react"
import { updateDashboard } from "../../models/app-actions"
import { ArtStates, InternalArtStates, toArtState, InternalArtStatesDescriptions } from "../../types/art-status"
import NoResults from "../../components/noResults/noResults"
import { ArtworkItemType } from "../../types/api-types"
import { getImageObject } from "../../utils/general";
import formatDateString from "../../utils/formatted-date"
import { CustomerServiceModal } from "../design-detail/customerServiceMessage"
import { serviceMapping } from "../../types/service-types"
import { getPersistedFilterData, markDashboardExitToDetails } from "../../utils/navigationPersistence"
import EditableInput from "../../components/editableInput/editableInput"
import { addAuthorizationHeader, getCustomerToken } from "../../api/network"
import FilterDropdown, { SelectedFilters } from "../design-detail/comps/filterOptionsDropdown"
import { getCheckboxDisplayText, getDocTypeLabel, isAWAWProductType, lnProducts } from "../../utils/artDetailsUtils"
import { CmeSize } from "../../utils/LNTransferDetailsSteps/CmeSize"
import CustomModal from "../../components/customMessageModal/customMsgModal"

// Styles
import statesStyles from "./art-filter.module.css";
import styles from './art.module.css';

export function DesignList() {
  const appSnap = useSnapshot(appModel);
  const docs = appSnap.designDocs as DesignDoc[];
  const artStatusFilter = appSnap.artStatusFilter
  const totalItems = appSnap.totalDesigns;
  const isLoading = appSnap.isPageLoading;
  const loaderArray = [...Array(12).keys()];
  const urlParams = getUrlParams();
  const customerId = appSnap.customerId || "";
  const searchText = appSnap.searchText || "";
  const [sideBarOpen, setSideBarOpen] = useState<boolean>(false);
  const archived = appSnap.artStatusFilter == ArtStates.ARCHIVED;
  const [openCSMsgModal, setOpenCSMsgModal] = useState<boolean>(false);
  const [currentDocId, setCurrentDocId] = useState<string>("");
  const [wizardType, setWizardType] = useState<string>('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    e: [], // EasyView products
    l: [], // LNTransfer products
    u: []  // WizardDesign products
  });

  const [missingMaterialModal, setMissingMaterialModal] = useState<undefined|JSX.Element>(undefined);
  const [closeWizardDataSettingSupported, setCloseWizardDataSettingSupported] = useState<boolean>(false);

  useEffect(() => {
    const filterData = getPersistedFilterData();
    const hasData = filterData && Object.values(filterData)?.some(
      (filter) => Array.isArray(filter) && filter.length > 0
    );

    hasData && setSelectedFilters(filterData);

    updateDashboard({
      ...(archived && { archived }),
      artState: artStatusFilter,
      searchText,
      products: hasData ? filterData : appModel.products
    });
  }, [archived]);

  const modalOnCloseDialogSetPromise = new Promise<void>((resolve) => {
    useEffect(() => {
      const messageHandler = closeWizardDataSettingSupported ? 
      (event: MessageEvent) => {
        const data = event.data;
        const hasData = !!data
        const isCloseWizardDataSet = hasData && data.closeWizardDataSet;
        if(isCloseWizardDataSet) {
          setCloseWizardDataSettingSupported(true);
        }
      }
      :
      (event: MessageEvent) => {
        const data = event.data;
        const hasData = !!data
        const isCloseWizardDataSet = hasData && data.closeWizardDataSet;
        if(isCloseWizardDataSet) {
          resolve();
        }
      };
      window.addEventListener("message", messageHandler);

      if(!closeWizardDataSettingSupported) {
        const timeoutId = setTimeout(() => {
          parent.postMessage({ closeWizardData: {
            showCloseWizardDialog: false
          } }, "*");
          clearTimeout(timeoutId);
        }, 100); // 0.1 seconds timeout
      }

      return () => {
        window.removeEventListener("message", messageHandler);
      };
    }, [closeWizardDataSettingSupported]);
  });

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterApply = async (filters: SelectedFilters) => {
    setSelectedFilters(filters);

    await updateDashboard({
      ...(archived && { archived }),
      artState: artStatusFilter,
      pageIndex: 0,
      searchText,
      products: filters
    });
  };

  const handleAddToCart = async (doc: DesignDoc) => {
    const { id, docType, wizardID, statusID, wizardData } = doc;

    const isAWAW = docType === "LNTransfer" && isAWAWProductType(wizardID)

    if (docType === "WizardDesign" && wizardID === 'WovenTag') {
      setWizardType(wizardID);
      setCurrentDocId(id);
      setOpenCSMsgModal(true);
      return;
    }

    let isOriginalMaterialUnavailable = false;
    const unavailableMaterials = ['FFE','CBL']
    if(isAWAW && (unavailableMaterials.includes(wizardData.material1) || unavailableMaterials.includes(wizardData.material2) || unavailableMaterials.includes(wizardData.material3))) {
      isOriginalMaterialUnavailable = true;
    }

    const wizardTypesWithUnavailableMaterials = ['CustomCadCut','CustomCutLetters','CustomCutNumbers']
    if(docType === "WizardDesign"
      && wizardTypesWithUnavailableMaterials.includes(wizardID)
      && (unavailableMaterials.includes(wizardData.color1Material) || unavailableMaterials.includes(wizardData.color2Material) || unavailableMaterials.includes(wizardData.color3Material))
    ) {
      isOriginalMaterialUnavailable = true;
    }

    if (isOriginalMaterialUnavailable) {
      const modal = <CustomModal
        isOpen={true}
        onClose={() => { setMissingMaterialModal(undefined) }}
        onCancel={() => { setMissingMaterialModal(undefined) }}
        title='Notice'
        formattedMessage={(
          <div className="flex flex-col text-sm gap-y-[1em]">
            <span>
              {"One or more materials used in this design are unavailable."}
            </span>
            <span>
              {"If you continue you will need to select alternative materials for those unavailable."}
            </span>
          </div>
        )}
        onConfirm={async () => {
          try {
            await addToCart(id, customerId, docType, wizardID, statusID);
            sessionStorage.removeItem("isGoBack");
          } catch (err) {
            console.log(err);
          }
          setMissingMaterialModal(undefined);
        }}
        confirmButtonText="Ok"
        showCancelButton={true}
        isCustomerView={false}
      />;

      setMissingMaterialModal(modal);
      return;
    }

    try {
      await addToCart(id, customerId, docType, wizardID, statusID);
      sessionStorage.removeItem("isGoBack");
    } catch (err) {
      console.log(err);
    }
  };

  const handleMenuClick = (value: boolean) => {
    setSideBarOpen(value);
  }

  const handleCardClick = () => {
    markDashboardExitToDetails({
      pageIndex: appSnap.pageIndex,
      searchText: appSnap.searchText,
      artStatusFilter: appSnap.artStatusFilter,
      products: appModel.products
    });
  };

  const handleArtNameChange = (designId: string, name: string) => {
    const headers = addAuthorizationHeader({
      'Content-Type': 'application/json'
    });

    return fetch(`${getBackendApiUrl()}/design-artwork-name`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        designId,
        name
      })
    }).then(async (processResp) => {
      if (!processResp.ok) throw new Error("Error changing artwork name " + await processResp.text())
      return
    });
  }

  const parseSizeDetails = (htmlString: string) => {
    if (!htmlString) return [];

    return htmlString
      .split('<br />')
      .filter(line => line.trim())
      .map(line => {
        const [label, value] = line.split(':').map(part => part.trim());
        return { label, value };
      });
  };

  const renderSizeContent = (
    lnSize: boolean,
    hasSize: boolean,
    wizardData: any,
    size: any) => {
    if (lnSize) {
      const sizeDetails = parseSizeDetails(CmeSize(wizardData)?.detail);
      return sizeDetails.map(({ label, value }, index) => (
        <div key={index} className={styles.cardDetails}>
          <span><b>{label}:</b></span>
          <span>{value}</span>
        </div>
      ));
    }

    return (
      <div className={styles.cardDetails}>
        <span><b>Size:</b></span>
        <span>
          {hasSize ? (
            <span>{size?.width ?? "--"} x {size?.height ?? "--"}</span>
          ) : (
            <span><i>unavailable</i></span>
          )}
        </span>
      </div>
    );
  };

  return (
    <div className={sideBarOpen ? styles.appContainer : ""}>
      {sideBarOpen && <div className={styles.overlay}></div>}
      <div className={styles.wrapper}>
        <Header customerId={customerId} onMenuToggle={(e) => handleMenuClick(e)} />


        {/* AI Assist DTF Artwork Uploader Banner */}
        <div className="relative mx-2 mt-4 flex items-center justify-between gap-4 overflow-hidden rounded-xl border border-blue-300 bg-blue-50 px-6 py-4 shadow-sm">
          {/* "NEW" diagonal corner ribbon */}
          <div className="absolute left-0 top-0 h-20 w-20 overflow-hidden rounded-tl-xl">
            <div
              className="absolute bg-blue-600 text-center text-[10px] font-extrabold tracking-widest text-white"
              style={{ top: '14px', left: '-22px', width: '80px', transform: 'rotate(-45deg)' }}
            >
              NEW
            </div>
          </div>

          <div className="flex items-center gap-4 pl-6 pt-2">
            {/* AI Sparkle icon */}
            <svg className="h-9 w-9 shrink-0 text-blue-500" viewBox="0 0 36 36" fill="currentColor" aria-hidden="true">
              <path d="M22 4 C22 4 21 11 18 14 C15 17 8 18 8 18 C8 18 15 19 18 22 C21 25 22 32 22 32 C22 32 23 25 26 22 C29 19 36 18 36 18 C36 18 29 17 26 14 C23 11 22 4 22 4Z" />
              <path d="M8 2 C8 2 7.4 5.6 6 7 C4.6 8.4 1 9 1 9 C1 9 4.6 9.6 6 11 C7.4 12.4 8 16 8 16 C8 16 8.6 12.4 10 11 C11.4 9.6 15 9 15 9 C15 9 11.4 8.4 10 7 C8.6 5.6 8 2 8 2Z" />
            </svg>
            <p className="text-sm text-gray-800">
              <span className="font-bold">STAHLS' AI Assist DTF Artwork Uploader:</span>{' '}
              We've introduced a new DTF artwork uploader to make your experience easier, faster, and more intuitive.
            </p>
          </div>
          <a
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 shrink-0 rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-800"
            onClick={async (e) => { 
              e.preventDefault();
              parent.postMessage({ closeWizardData: {
                confirmCloseOnLeaveMessage: "The designs that weren't added to the cart will be discarded. Are you sure you want to close?",
                showCloseWizardDialog: true
              } }, "*");
              if (closeWizardDataSettingSupported) await modalOnCloseDialogSetPromise;
              const DWS_URL = getDWSUrl();
              const dwsWizard = "https://" + DWS_URL;

              const params = new URLSearchParams(window.location.search);
              params.append('source', 'dashboard');
              const cookieValues: string[] = [];

              params.forEach((value, key) => {
                cookieValues.push(`__dws_${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
              });

              localStorage.setItem('dws-cookie', cookieValues.join('; '));

              const currentLang = getLanguage()
              const lang = currentLang ? `&lang=${currentLang}` : ""

              window.location.replace(`${dwsWizard}/index-hawk-dtf.html?customerId=${customerId}&source=dashboard&customerToken=${getCustomerToken()}${lang}`);
            }}
          >
            AI ASSIST DTF UPLOADER
          </a>
        </div>

        <div className={`${styles.pfWrapper} w-full flex flex-row mt-4 mb-4 items-center px-2 justify-between`}>
          <div className={`${styles.filterContainer} flex flex-row`}>
            {/* Filter options dropdown */}
            <FilterDropdown
              isOpen={isFilterOpen}
              selectedFilters={selectedFilters}
              onToggle={handleFilterToggle}
              onApply={handleFilterApply}
            />
          </div>

          <div className={`${styles.pagerContainer} flex flex-row`}>
            {/* Pagination */}
            <Pager
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentState={artStatusFilter}
              searchText={searchText}
              filter={selectedFilters}
            />
          </div>
        </div>

        {!isLoading && docs.length === 0 && (
          <NoResults
            isSearch={searchText !== ''}
            searchText={searchText}
            artStatusFilter={artStatusFilter}
          />
        )}

        <ul role="list" className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 sm:gap-x-4 md:grid-cols-3 md:gap-x-6 lg:grid-cols-4 xl:gap-x-8">
          {!isLoading ? docs?.map((doc: DesignDoc) => {
            const { wizardID, wizardData, statusID } = doc;
            const hasAutoFixData = "autoFixData" in wizardData;
            const autoFixJobId = hasAutoFixData ? wizardData?.autoFixData?.JobId : undefined;
            const statusName = InternalArtStatesDescriptions[statusID].description;
            const isCustomProduct = wizardData.serviceType == "CustomProduct"
            const productType = isCustomProduct ? "Uploaded file"
              : wizardData && (getCheckboxDisplayText(wizardID) ?? (getCheckboxDisplayText(wizardData.productType) ?? wizardData.productType));
            const artName = wizardData && wizardData.artName;
            const hasArtName = typeof artName != 'undefined'
            const statusColor = getColor(toArtState(statusID)) ?? "";
            const canAddToCart = (statusID === InternalArtStates.APPROVED || statusID === InternalArtStates.REVIEW) && !archived;
            const isIncomplete = statusID === InternalArtStates.INCOMPLETE;
            const artworkTypes = convertDesignDocToImageObject(doc);
            const isBanner = wizardID === "Banners";
            const height = wizardData && parseFloat(wizardData.artHeight);
            const width = wizardData && parseFloat(wizardData.artWidth);
            const fontHeight = wizardData && parseFloat(wizardData?.fontHeight);
            const bannerWidth = isBanner ? parseFloat(wizardData.bannerWidth) : undefined;
            const bannerHeight = isBanner ? parseFloat(wizardData.bannerHeight) : undefined;
            const imageObjects = getImageObject(artworkTypes, DEFAULT_IMAGE);
            const imgSrc = Array.isArray(imageObjects) ? imageObjects[0] : DEFAULT_IMAGE;
            const imgUrl = generateImageSrcFromArtwork(imgSrc as ArtworkItemType, DEFAULT_IMAGE)
            const addedToCartDate = doc?.addedToCartDate;
            const formattedDate = addedToCartDate ? formatDateString({ dateString: addedToCartDate }) : "";
            const serviceType = serviceMapping[wizardData?.serviceType] ?? wizardData?.serviceType ?? "--";
            const source = getDocTypeLabel(doc?.docType);
            const submittedDate = doc?.createdDate ? formatDateString({ dateString: doc?.createdDate }) : "--";
            const lnSize = doc?.docType === "LNTransfer" && lnProducts.includes(wizardID ?? wizardData?.productType);

            const size = {
              height: fontHeight || (isBanner ? bannerHeight : height) || null,
              width: fontHeight ? null : (isBanner ? bannerWidth : width) || null,
            };

            const hasSize = !!size.width || !!size.height

            return (
              <li key={doc.id} className="relative p-1">
                <div className={`${styles.card} flex flex-col`}>
                  <a
                    onClick={handleCardClick}
                    href={`?artId=${doc.id}&${urlParams}`}
                    className="group aspect-h-7 aspect-w-10 block w-full overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100">
                    <img src={imgUrl} alt="" className="pointer-events-none object-cover group-hover:opacity-75" />
                    <button type="button" className={`absolute inset-0 focus:outline-none`}>
                      <span className="sr-only">View details for {doc.id}</span>
                    </button>
                  </a>
                  <div className={[styles.statusBar, (statusColor ? statesStyles[statusColor] : "")].join(' ')}></div>

                  {/* Artwork details */}
                  <div className="flex flex-col h-full">
                    <div className={styles.cardBody}>
                      <div className={styles.cardDetails}>
                        <span><b>Artwork Name:</b></span>
                        <span>{hasArtName ? <EditableInput text={artName} onChange={changedText => handleArtNameChange(doc.id, changedText)}></EditableInput> : <i>unavailable</i>}</span>
                      </div>
                      <div className={styles.cardDetails}>
                        <span><b>Design ID:</b></span>
                        <span>{doc.id ?? <i>unavailable</i>}</span>
                      </div>
                      <div className={styles.cardDetails}>
                        <span><b>Status:</b></span>
                        <span>{statusName}</span>
                      </div>
                      {renderSizeContent(lnSize, hasSize, wizardData, size)}
                      <div className={styles.cardDetails}>
                        <span><b>Service Type:</b></span>
                        <span>{serviceType}</span>
                      </div>
                      <div className={styles.cardDetails}>
                        <span><b>Product Type:</b></span>
                        <span>{productType ?? <i>unavailable</i>}</span>
                      </div>
                      <div className={styles.cardDetails}>
                        <span><b>Source:</b></span>
                        <span>{source}</span>
                      </div>
                      <div className={styles.cardDetails}>
                        <span><b>Date Art Submitted:</b></span>
                        <span>{submittedDate}</span>
                      </div>
                      <div className={styles.cardDetails}>
                        <span><b>Last Viewed:</b></span>
                        <span>{formattedDate ?? <i>unavailable</i>}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-3 p-2 pb-6 mt-auto">
                      <a href={canAddToCart || isIncomplete ? "#" : `?artId=${doc.id}&${urlParams}`} onClick={(e) => {
                        if (isIncomplete) {
                          if(autoFixJobId) {
                            e.preventDefault();
                            const DWS_URL = getDWSUrl();
                            const dwsWizard = "https://" + DWS_URL;

                            const params = new URLSearchParams(window.location.search);
                            params.append('source', 'dashboard');
                            const cookieValues: string[] = [];

                            params.forEach((value, key) => {
                              cookieValues.push(`__dws_${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
                            });

                            localStorage.setItem('dws-cookie', cookieValues.join('; '));

                            const currentLang = getLanguage()
                            const lang = currentLang ? `&lang=${currentLang}` : ""

                            window.location.replace(`${dwsWizard}/index-hawk-dtf-edit-saved.html?customerId=${customerId}&customerToken=${getCustomerToken()}${lang}&designId=${doc.id}&editJob=${autoFixJobId}`);
                            return;
                          }

                          e.preventDefault();
                          // Redirect to upload page
                          const DWS_URL = getDWSUrl();
                          const dwsWizard = "https://" + DWS_URL;

                          const params = new URLSearchParams(window.location.search);
                          params.append('source', 'dashboard');
                          const cookieValues: string[] = [];

                          params.forEach((value, key) => {
                            cookieValues.push(`__dws_${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
                          });

                          localStorage.setItem('dws-cookie', cookieValues.join('; '));

                          const currentLang = getLanguage()
                          const lang = currentLang ? `&lang=${currentLang}` : ""

                          window.location.replace(`${dwsWizard}?customerId=${customerId}&source=dashboard&customerToken=${getCustomerToken()}${lang}&saved=true&designId=${doc.id}`);
                          return;
                        }
                        else if (canAddToCart) {
                          e.preventDefault();
                          if (doc.id) {
                            handleAddToCart(doc);
                          }
                        } else {
                          handleCardClick()
                        }
                      }}>
                        <div className={styles.dashBtnWrapper}>
                          <button
                            type="button"
                            className={`${styles.orangeBtn} flex flex-row gap-1 text-white hover:bg-blue-500`}>
                            {isIncomplete ? "Resume Upload" : canAddToCart ? "Add to Cart" : "Review"}
                          </button>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </li>
            );
          })
            :
            loaderArray?.map((i) => (
              <li key={i} className="relative">
                <SkeletonLoader />
              </li>
            ))}
        </ul>
      </div>

      {currentDocId && <CustomerServiceModal
        isOpen={openCSMsgModal}
        onClose={() => setOpenCSMsgModal(false)}
        docId={currentDocId}
        customerId={customerId}
        wizardType={wizardType}
      />}

      {missingMaterialModal}

    </div>
  );
}